import { useState, useCallback, useRef, useEffect } from 'react';
import { scopeCoursesToUser } from '../utils/scope';
import { getCoursesAdmin } from '../api/courses';
import { useAuth } from '../context/AuthContext';

const SEARCH_DEBOUNCE_MS = 300;
// Pages read for one "load more" before giving up on filling it (see fetchCourses).
const MAX_PAGES_PER_FETCH = 10;

/**
 * Custom hook for searchable, paginated course selection.
 * Loads first 20 courses, then loads more on scroll or search.
 * @param {{ limit?: number, initialSearch?: string }} options
 * @returns {{
 *   courses: Array,
 *   loading: boolean,
 *   error: string | null,
 *   search: string,
 *   setSearch: function,
 *   hasMore: boolean,
 *   loadMore: function,
 *   refetch: function,
 *   offset: number,
 *   total: number
 * }}
 */
export function useSearchableCourses(options = {}) {
  const { limit = 20, initialSearch = '' } = options;
  const { user } = useAuth();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(initialSearch);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  // Only the latest request may touch the list; an earlier, slower one is dropped.
  const requestIdRef = useRef(0);
  // Next server page to read, so "load more" continues where the last read stopped.
  const nextPageRef = useRef(1);
  const firstRunRef = useRef(true);

  const fetchCourses = useCallback(async (reset, term) => {
    const requestId = ++requestIdRef.current;
    const startPage = reset ? 1 : nextPageRef.current;

    try {
      setLoading(true);
      setError(null);

      // B2B scoping: staff see the courses they own; admins see the whole catalogue. The server
      // pages the whole catalogue, so for staff a page can hold few or none of their courses
      // while later pages still do: keep reading until a page's worth is found or it ends.
      let page = startPage;
      let more = true;
      let totalCount = 0;
      const found = [];
      while (more && found.length < limit && page - startPage < MAX_PAGES_PER_FETCH) {
        const response = await getCoursesAdmin({ limit, page, ...(term && { search: term }) });
        if (requestId !== requestIdRef.current) return;

        const rows = response?.data || [];
        found.push(...scopeCoursesToUser(rows, user));
        const meta = response?.meta;
        totalCount = meta?.total ?? totalCount;
        more = meta ? page < meta.totalPages : rows.length >= limit;
        page += 1;
      }

      nextPageRef.current = page;
      setCourses((prev) => {
        if (reset) return found;
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...found.filter((c) => !seen.has(c.id))];
      });
      setOffset((page - 1) * limit);
      setTotal(totalCount);
      setHasMore(more);
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setError(err.message || 'Failed to load courses');
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [limit, user]);

  // Loads on mount (at once) and again, after a pause, whenever the search text changes.
  useEffect(() => {
    const delay = firstRunRef.current ? 0 : SEARCH_DEBOUNCE_MS;
    firstRunRef.current = false;
    const timer = setTimeout(() => {
      fetchCourses(true, search);
    }, delay);
    return () => clearTimeout(timer);
  }, [search, fetchCourses]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      fetchCourses(false, search);
    }
  }, [search, hasMore, loading, fetchCourses]);

  const refetch = useCallback(() => {
    fetchCourses(true, search);
  }, [search, fetchCourses]);

  return {
    courses,
    loading,
    error,
    search,
    setSearch,
    hasMore,
    loadMore,
    refetch,
    offset,
    total
  };
}
